import React from "react";
import {
  Box,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack,
  Delete,
  Edit,
  Visibility,
  CheckCircle,
} from "@mui/icons-material";

interface Coluna {
  id: string;
  label: string;
}

interface TabelaProps {
  titulo: string;
  colunas: Coluna[];
  dados: any[];
  onAdd?: () => void;
  onEdit?: (id: string) => void;
  onView?: (id: string) => void;
  onDelete?: (id: string) => void;
  onActivate?: (id: string) => void;
  loading: boolean;
  showVisibilityIcon?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

const Tabela: React.FC<TabelaProps> = ({
  titulo,
  colunas,
  dados,
  onAdd,
  onEdit,
  onView,
  onDelete,
  onActivate,
  loading,
  showVisibilityIcon,
  showBackButton,
  onBack,
}) => {
  const [openConfirmationDialog, setOpenConfirmationDialog] =
    React.useState(false);
  const [itemToDeleteId, setItemToDeleteId] = React.useState<string | null>(
    null
  );

  const handleOpenConfirmationDialog = (id: string) => {
    setItemToDeleteId(id);
    setOpenConfirmationDialog(true);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {showBackButton && onBack && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBack />}
            onClick={onBack}
          >
            Voltar
          </Button>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h5" sx={{ mb: { xs: 2, sm: 0 } }}>
          {titulo}
        </Typography>
        {onAdd && (
          <Button variant="contained" color="primary" onClick={onAdd}>
            Adicionar
          </Button>
        )}
      </Box>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "60vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: { xs: 300, sm: 650 } }}>
              <TableHead>
                <TableRow>
                  {colunas.map((coluna) => (
                    <TableCell
                      key={coluna.id}
                      sx={{
                        whiteSpace: "nowrap",
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {coluna.label}
                    </TableCell>
                  ))}
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      maxWidth: "150px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dados.map((dado, index) => (
                  <TableRow key={index}>
                    {colunas.map((coluna) => (
                      <TableCell
                        key={coluna.id}
                        sx={{
                          whiteSpace: "nowrap",
                          maxWidth: "150px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {coluna.id.includes(".")
                          ? coluna.id
                              .split(".")
                              .reduce((obj, key) => obj && obj[key], dado)
                          : dado[coluna.id]}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {onEdit && (
                        <IconButton
                          onClick={() => onEdit(dado.id)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      )}
                      {showVisibilityIcon && onView && (
                        <IconButton
                          onClick={() => onView(dado.id)}
                          color="default"
                        >
                          <Visibility />
                        </IconButton>
                      )}
                      {onDelete &&
                        (!dado.ativo_usuario ||
                          dado.ativo_usuario === "Sim") && (
                          <IconButton
                            onClick={() =>
                              handleOpenConfirmationDialog(dado.id)
                            }
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        )}
                      {onActivate && dado.ativo_usuario === "Não" && (
                        <IconButton
                          onClick={() => onActivate(dado.id)}
                          color="success"
                        >
                          <CheckCircle />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog
            open={openConfirmationDialog}
            onClose={() => setOpenConfirmationDialog(false)}
          >
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                Tem certeza de que deseja excluir este item?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenConfirmationDialog(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (onDelete && itemToDeleteId) {
                    onDelete(itemToDeleteId);
                  }
                  setOpenConfirmationDialog(false);
                }}
                color="error"
              >
                Confirmar
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default Tabela;
